"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Search, Check, X } from "lucide-react";

const OTHER_TIP_ROLES = ['Cashier', 'Hosts', 'Floor Supervisor'];

const resolveOtherTipRole = (roleString) => {
  if (!roleString) return 'Cashier';
  const roles = String(roleString).split(',').map((r) => r.trim()).filter(Boolean);
  const match = OTHER_TIP_ROLES.find((role) => roles.includes(role));
  return match || roles[0] || 'Cashier';
};

const OtherTipsAddModal = ({ 
  isOpen, 
  onClose, 
  onAdd, 
  employees, 
  currentUser, 
  calculateCommission 
}) => {
  const [formData, setFormData] = useState({
    empname: "",
    emprole: "",
    amountcash: "",
  });
  const [tax, setTax] = useState(0);
  const [paidOutAmount, setPaidOutAmount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({ empname: "", emprole: "", amountcash: "" });
      setError(null);
      setSearchTerm("");
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Memoize the calculation function to prevent unnecessary re-renders
  const memoizedCalculateCommission = useCallback((amount, employeeRole, transactionDate = new Date()) => {
    return calculateCommission(amount, employeeRole, transactionDate);
  }, [calculateCommission]);

  // Calculate tax and payout
  useEffect(() => {
    const amt = parseFloat(formData.amountcash) || 0;
    const calculatedTax = memoizedCalculateCommission(amt, formData.emprole, new Date());
    setTax(calculatedTax);
    setPaidOutAmount(amt - calculatedTax);
  }, [formData.amountcash, formData.emprole, memoizedCalculateCommission]);

  // Filter employees with proper null/undefined checks
  const filteredEmployees = useMemo(() => {
    if (!employees || !Array.isArray(employees)) {
      return [];
    }
    
    return employees.filter(emp => {
      // Handle both string and object formats
      if (typeof emp === 'string') {
        return emp.toLowerCase().includes(searchTerm.toLowerCase());
      } else if (emp && typeof emp === 'object' && emp.name) {
        return emp.name.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return false;
    });
  }, [employees, searchTerm]);

  const handleEmployeeSelect = (emp) => {
    // Handle both string and object formats
    const employeeName = typeof emp === 'string' ? emp : emp.name;
    const employeeRole = typeof emp === 'string' ? 'Cashier' : resolveOtherTipRole(emp.role);
    setFormData(prev => ({ 
      ...prev, 
      empname: employeeName,
      emprole: employeeRole
    }));
    setDropdownOpen(false);
    setSearchTerm("");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSelection = () => {
    setFormData(prev => ({ ...prev, empname: "", emprole: "" }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.empname || !formData.amountcash) {
      setError("Please fill all required fields");
      return;
    }
  
    const amount = parseFloat(formData.amountcash);
    if (isNaN(amount) || amount <= 0) {
      setError("Please enter a valid amount");
      return;
    }
  
    setSubmitting(true);
    setError(null);
    
    try {
      await onAdd({
        empname: formData.empname,
        emprole: formData.emprole,
        amountcash: amount,  // Send as number
        owetotoast: 0,
        tax: tax, // Include the calculated tax amount
        paidout: paidOutAmount, // Include the calculated paid out amount
        added_by: currentUser ? currentUser.username : ""
      });
      setFormData({ empname: "", emprole: "", amountcash: "" });
      onClose();
    } catch (err) {
      setError("Failed to add record. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Tip Record</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee Name *
            </label>
            
            <div className="relative" ref={dropdownRef}>
              <div 
                className="flex items-center justify-between border rounded-md p-2 cursor-pointer bg-white"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                {formData.empname ? (
                  <div className="flex items-center">
                    <span className="mr-2">{formData.empname}</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClearSelection();
                      }}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400">Select employee</span>
                )}
                <svg 
                  className={`w-4 h-4 ml-2 transform transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              
              {dropdownOpen && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  <div className="sticky top-0 bg-white p-2 border-b">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Search employees..."
                        className="pl-8"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        autoFocus
                      />
                    </div>
                  </div>
                  
                  <div className="py-1">
                    {filteredEmployees.length > 0 ? (
                      filteredEmployees.map((emp, index) => {
                        const employeeName = typeof emp === 'string' ? emp : emp.name;
                        const employeeRole = typeof emp === 'string' ? "" : (emp.role || "");
                        const employeeKey = typeof emp === 'string' ? emp : emp.id || emp.name || index;
                        
                        return (
                          <div
                            key={employeeKey}
                            className={`flex items-center p-2 cursor-pointer hover:bg-gray-100 ${
                              formData.empname === employeeName ? "bg-gray-100" : ""
                            }`}
                            onClick={() => handleEmployeeSelect(emp)}
                          >
                            <div className={`w-5 h-5 flex items-center justify-center border rounded mr-2 ${
                              formData.empname === employeeName 
                                ? "bg-blue-500 border-blue-500" 
                                : "border-gray-300"
                            }`}>
                              {formData.empname === employeeName && (
                                <Check className="h-4 w-4 text-white" />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-gray-900">{employeeName}</span>
                              {employeeRole && (
                                <span className="text-xs text-gray-500">{employeeRole}</span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-3 text-center text-gray-500">
                        No employees found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {formData.emprole && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
              <div className="text-sm p-2 border border-gray-300 rounded-md bg-gray-50">
                {formData.emprole}
              </div>
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount *
            </label>
            <Input
              name="amountcash"
              type="number"
              value={formData.amountcash}
              onChange={handleInputChange}
              placeholder="Enter amount"
              className="w-full"
              min="0.01"
              step="0.01"
              required
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {formData.emprole === "Floor Supervisor" ? "Tax (5%)" : "Tax"}
              </label>
              <div className="text-lg text-red-500 font-semibold">
                -${tax.toFixed(2)}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Out
              </label>
              <div className="text-lg text-green-500 font-semibold">
                ${paidOutAmount.toFixed(2)}
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : "Add Record"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherTipsAddModal;